/* Who is behind the page, and the full bibliography it stands on.
 *
 * The modal is a dialog, so it behaves like one: focus moves into it, Escape
 * closes it, Tab cycles inside it, and focus returns to the button that opened
 * it. Nothing here is a decorative overlay.
 */
import { Fragment, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { bibLabel, bibliography } from '../engine/sources';
import { useApp } from '../state/AppProvider';

const PROFILES = [
  {
    key: 'linkLinkedin',
    aria: 'ariaLinkedin',
    href: 'https://www.linkedin.com/in/charlie-%F0%9F%A6%8A-cohen-47b241a2/',
  },
  { key: 'linkSite', aria: 'ariaSite', href: 'https://charlie-cohen.com' },
  { key: 'linkGithub', aria: 'ariaGithub', href: 'https://github.com/Linzell' },
];

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export function AboutModal() {
  const { t } = useTranslation();
  const { about } = useApp();
  const scrimRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const restoreTo = useRef<Element | null>(null);

  useEffect(() => {
    if (!about.open) return;
    restoreTo.current = document.activeElement;
    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        about.hide();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = scrimRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const back = restoreTo.current;
      if (back instanceof HTMLElement) back.focus();
    };
  }, [about]);

  if (!about.open) return null;

  const paragraphs = t('aboutBody', { returnObjects: true }) as string[];

  return (
    <div
      className="scrim"
      id="scrim"
      ref={scrimRef}
      onClick={(event) => {
        if (event.target === scrimRef.current) about.hide();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="modal-title" data-od-id="about-modal">
        <div className="modal-head">
          <div>
            <p className="eyebrow" id="modal-eyebrow">
              {t('hand')}
            </p>
            <h2 className="h2" id="modal-title">
              {t('aboutTitle')}
            </h2>
          </div>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            data-act="close-about"
            id="modal-close"
            ref={closeRef}
            onClick={about.hide}
          >
            {t('close')}
          </button>
        </div>

        <div className="modal-body" id="modal-body">
          <div className="idcard">
            <span className="idmono" aria-hidden="true">
              CC
            </span>
            <div>
              <p className="idname">{t('aboutName')}</p>
              <p className="idrole">{t('aboutRole')}</p>
              <p className="idplace">{t('aboutPlace')}</p>
            </div>
          </div>

          <div className="stack">
            <p className="eyebrow">{t('profilesEyebrow')}</p>
            <div className="profiles">
              {PROFILES.map((profile) => (
                <a
                  key={profile.key}
                  href={profile.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t(profile.aria)}
                >
                  {t(profile.key)}
                </a>
              ))}
            </div>
          </div>

          {paragraphs.map((paragraph, i) => (
            <p key={i}>{paragraph}</p>
          ))}

          <div className="stack">
            <p className="eyebrow">{t('sourcesTitle')}</p>
            {/* Flat siblings, not one wrapper per subject: the stack's 10px gap
                is what separates every row, so nesting the groups would collapse
                that spacing. */}
            {bibliography().map((group) => (
              <Fragment key={group.topic}>
                <p className="srctopic">{t(`topics:${group.topic}`)}</p>
                {group.rows.map((row) => (
                  <div key={row.id || row.v || row.t} className="srcline">
                    <b>{bibLabel(row)}</b>
                    <span>{row.t}</span>
                  </div>
                ))}
              </Fragment>
            ))}
          </div>

          <p className="credit">{t('credit')}</p>
        </div>
      </div>
    </div>
  );
}
